/**
 * City Beauty Instagram Section - Shopify Compatible JavaScript
 * Handles Instagram section functionality including lazy loading, LightWidget integration, and analytics
 */

(function() {
    'use strict';

    // Instagram section configuration
    const instagramConfig = {
        sectionSelector: '.citybeauty-instagram-section',
        lazyClass: 'lazy',
        loadedClass: 'loaded',
        animateClass: 'animate',
        dataLoader: 'component',
        lightWidget: {
            scriptUrl: 'https://cdn.lightwidget.com/widgets/lightwidget.js',
            widgetId: '1727cf9af9a357789334ff5156361b2a',
            iframeUrl: 'https://cdn.lightwidget.com/widgets/1727cf9af9a357789334ff5156361b2a.html'
        },
        defaultSettings: {
            instagramHandle: '@citybeautyofficial',
            hashtag: '#citybeauty',
            instagramUrl: 'https://www.instagram.com/citybeautyofficial/',
            title: 'Get Social with <br class="mobile"><i>City Beauty</i><sup>®</sup>',
            description: 'Get social with <b><a href="https://www.instagram.com/citybeautyofficial/" target="_blank" class="gold">@citybeautyofficial</a></b> on Instagram and tag <b><a href="https://www.instagram.com/citybeautyofficial/" target="_blank" class="gold">#citybeauty</a></b> so we can flaunt you in our feed.'
        }
    };

    /**
     * Initialize Instagram section components
     */
    function initCityBeautyInstagramComponents() {
        console.log('Initializing City Beauty Instagram components...');
        
        // Initialize lazy loading
        initLazyLoading();
        
        // Initialize animations
        initAnimations();
        
        // Initialize LightWidget
        initLightWidget();
        
        // Initialize link tracking
        initLinkTracking();
        
        // Handle responsive behavior
        handleInstagramResize();
        
        console.log('City Beauty Instagram components initialized successfully');
    }

    /**
     * Initialize lazy loading for the section
     */
    function initLazyLoading() {
        const sections = document.querySelectorAll(`${instagramConfig.sectionSelector}.${instagramConfig.lazyClass}`);
        
        if (sections.length === 0) {
            return;
        }

        // Check if Intersection Observer is supported
        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        loadSection(entry.target);
                        observer.unobserve(entry.target);
                    }
                });
            }, {
                root: null,
                rootMargin: '50px',
                threshold: 0.1
            });

            sections.forEach(section => {
                observer.observe(section);
            });
        } else {
            // Fallback for browsers without Intersection Observer
            sections.forEach(section => {
                loadSection(section);
            });
        }
    }

    /**
     * Load a lazy section
     */
    function loadSection(section) {
        // Remove lazy class and add loaded class
        section.classList.remove(instagramConfig.lazyClass);
        section.classList.add(instagramConfig.loadedClass);
        
        // Trigger animation after a short delay
        setTimeout(() => {
            section.classList.add(instagramConfig.animateClass);
        }, 100);
        
        // Load LightWidget if not already loaded
        loadLightWidget(section);
        
        console.log('Instagram section loaded');
    }

    /**
     * Initialize animations
     */
    function initAnimations() {
        const sections = document.querySelectorAll(instagramConfig.sectionSelector);
        
        sections.forEach(section => {
            // Add animation class if section is already visible
            if (isElementInViewport(section)) {
                section.classList.add(instagramConfig.animateClass);
            }
        });
    }

    /**
     * Check if element is in viewport
     */
    function isElementInViewport(el) {
        const rect = el.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    /**
     * Initialize LightWidget
     */
    function initLightWidget() {
        const sections = document.querySelectorAll(instagramConfig.sectionSelector);
        
        sections.forEach(section => {
            const lightWidgetBox = section.querySelector('.lightwidget-box');
            
            if (lightWidgetBox && !lightWidgetBox.querySelector('iframe')) {
                // Add loading state
                lightWidgetBox.classList.add('loading');
                
                // Load LightWidget script if not already loaded
                if (!document.querySelector(`script[src="${instagramConfig.lightWidget.scriptUrl}"]`)) {
                    loadLightWidgetScript();
                } else {
                    // Script already loaded, create iframe
                    createLightWidgetIframe(lightWidgetBox);
                }
            }
        });
    }

    /**
     * Load LightWidget script
     */
    function loadLightWidgetScript() {
        const script = document.createElement('script');
        script.src = instagramConfig.lightWidget.scriptUrl;
        script.async = true;
        script.onload = () => {
            console.log('LightWidget script loaded');
            // Create iframes for all sections
            const sections = document.querySelectorAll(instagramConfig.sectionSelector);
            sections.forEach(section => {
                const lightWidgetBox = section.querySelector('.lightwidget-box');
                if (lightWidgetBox) {
                    createLightWidgetIframe(lightWidgetBox);
                }
            });
        };
        script.onerror = () => {
            console.error('Failed to load LightWidget script');
            // Show error state
            const sections = document.querySelectorAll(instagramConfig.sectionSelector);
            sections.forEach(section => {
                const lightWidgetBox = section.querySelector('.lightwidget-box');
                if (lightWidgetBox) {
                    showLightWidgetError(lightWidgetBox);
                }
            });
        };
        document.head.appendChild(script);
    }

    /**
     * Create LightWidget iframe
     */
    function createLightWidgetIframe(lightWidgetBox) {
        // Remove loading state
        lightWidgetBox.classList.remove('loading');
        
        // Create iframe
        const iframe = document.createElement('iframe');
        iframe.src = instagramConfig.lightWidget.iframeUrl;
        iframe.scrolling = 'no';
        iframe.allowTransparency = 'true';
        iframe.className = 'lightwidget-widget';
        iframe.style.cssText = 'width:100%;border:0;overflow:hidden;';
        
        // Add load event listener
        iframe.onload = () => {
            console.log('LightWidget iframe loaded');
            lightWidgetBox.classList.remove('loading');
        };
        
        iframe.onerror = () => {
            console.error('Failed to load LightWidget iframe');
            showLightWidgetError(lightWidgetBox);
        };
        
        // Append iframe
        lightWidgetBox.appendChild(iframe);
    }

    /**
     * Show LightWidget error state
     */
    function showLightWidgetError(lightWidgetBox) {
        lightWidgetBox.classList.remove('loading');
        lightWidgetBox.classList.add('error');
    }

    /**
     * Load LightWidget for a specific section
     */
    function loadLightWidget(section) {
        const lightWidgetBox = section.querySelector('.lightwidget-box');
        
        if (lightWidgetBox && !lightWidgetBox.querySelector('iframe')) {
            // Add loading state
            lightWidgetBox.classList.add('loading');
            
            // Load LightWidget script if not already loaded
            if (!document.querySelector(`script[src="${instagramConfig.lightWidget.scriptUrl}"]`)) {
                loadLightWidgetScript();
            } else {
                // Script already loaded, create iframe
                createLightWidgetIframe(lightWidgetBox);
            }
        }
    }

    /**
     * Initialize link tracking
     */
    function initLinkTracking() {
        const sections = document.querySelectorAll(instagramConfig.sectionSelector);
        
        sections.forEach(section => {
            const links = section.querySelectorAll('a');
            
            links.forEach(link => {
                link.addEventListener('click', (e) => {
                    trackInstagramLink(link.href, link.textContent.trim());
                });
            });
        });
    }

    /**
     * Track Instagram link clicks
     */
    function trackInstagramLink(url, text) {
        console.log(`Instagram link clicked: ${text} - ${url}`);
        
        // Add analytics tracking here if needed
        // Example: gtag('event', 'click', { 'event_category': 'instagram', 'event_label': text });
        
        // Optional: Add custom event for other tracking systems
        const event = new CustomEvent('instagramLinkClick', {
            detail: {
                url: url,
                text: text,
                timestamp: Date.now()
            }
        });
        document.dispatchEvent(event);
    }

    /**
     * Handle responsive behavior
     */
    function handleInstagramResize() {
        const sections = document.querySelectorAll(instagramConfig.sectionSelector);
        
        if (sections.length === 0) {
            return;
        }

        function updateResponsiveDisplay() {
            sections.forEach(section => {
                const title = section.querySelector('.title1');
                const description = section.querySelector('.medium, .large');
                
                if (title) {
                    // Update title font size based on screen size
                    if (window.innerWidth < 576) {
                        title.style.fontSize = '24px';
                    } else if (window.innerWidth < 768) {
                        title.style.fontSize = '28px';
                    } else {
                        title.style.fontSize = '32px';
                    }
                }
                
                if (description) {
                    // Update description font size based on screen size
                    if (window.innerWidth < 576) {
                        description.style.fontSize = '13px';
                    } else if (window.innerWidth < 768) {
                        description.style.fontSize = '14px';
                    } else {
                        description.style.fontSize = '16px';
                    }
                }
            });
        }

        // Initial call
        updateResponsiveDisplay();

        // Listen for resize events
        window.addEventListener('resize', debounce(updateResponsiveDisplay, 250));
    }

    /**
     * Debounce function to limit function calls
     */
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Initialize accessibility features
     */
    function initAccessibility() {
        const sections = document.querySelectorAll(instagramConfig.sectionSelector);
        
        sections.forEach(section => {
            const links = section.querySelectorAll('a');
            
            links.forEach(link => {
                // Add ARIA labels for screen readers
                if (link.href.includes('instagram.com')) {
                    link.setAttribute('aria-label', `Visit City Beauty on Instagram: ${link.textContent.trim()}`);
                }
                
                // Ensure proper tab order
                link.setAttribute('tabindex', '0');
            });
            
            // Add role to LightWidget container
            const lightWidgetBox = section.querySelector('.lightwidget-box');
            if (lightWidgetBox) {
                lightWidgetBox.setAttribute('role', 'region');
                lightWidgetBox.setAttribute('aria-label', 'Instagram feed');
            }
        });
    }

    /**
     * Generate Instagram section HTML
     */
    function generateInstagramHTML(settings = {}) {
        const config = { ...instagramConfig.defaultSettings, ...settings };
        
        return `
            <div class="container">
                <p class="title1" style="font-size: 32px;">${config.title}</p>
                <p class="medium">${config.description}</p>
            </div>
            <div class="lightwidget-box mt-4">
                <!-- LightWidget WIDGET -->
                <script src="${instagramConfig.lightWidget.scriptUrl}"></script>
                <iframe src="${instagramConfig.lightWidget.iframeUrl}" scrolling="no" allowtransparency="true" class="lightwidget-widget" style="width:100%;border:0;overflow:hidden;"></iframe>
            </div>
        `;
    }

    /**
     * Populate section with default content (if section is empty)
     */
    function populateInstagramContent() {
        const sections = document.querySelectorAll(instagramConfig.sectionSelector);
        
        sections.forEach(section => {
            const container = section.querySelector('.container');
            const lightWidgetBox = section.querySelector('.lightwidget-box');
            
            if (!container || !lightWidgetBox) {
                // Generate HTML for the section
                const html = generateInstagramHTML();
                section.innerHTML = html;
            }
        });
    }

    /**
     * Initialize all components
     */
    function initAll() {
        initCityBeautyInstagramComponents();
        initAccessibility();
        populateInstagramContent();
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAll);
    } else {
        initAll();
    }

    // Export functions for external use
    window.CityBeautyInstagram = {
        init: initCityBeautyInstagramComponents,
        loadSection: loadSection,
        handleResize: handleInstagramResize,
        initLazyLoading: initLazyLoading,
        initAnimations: initAnimations,
        initLightWidget: initLightWidget,
        trackInstagramLink: trackInstagramLink,
        populateInstagramContent: populateInstagramContent,
        generateHTML: generateInstagramHTML
    };

})();
